import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { PoweredBy } from '../components/Brand';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CERT_LIMITS,
  CERT_ROLES,
  CERT_TYPES,
  type CertDetails,
  type CertRequestRow,
  type CertType,
  type CertificateRow,
  type DeathBurialDetails,
  type MembershipDetails,
  type NikahNocDetails,
} from '@mahallu/shared';
import { ApiError, certs, loadErrorKey, useCertRequests, useHouse, useHouses, useIssuedCertificates, useMe, usePublicCertificate } from '../api';
import { FormFooter, FormSection, Modal, SelectField, TextAreaField, TextField } from '../components/Form';
import { Icon } from '../components/Icon';
import { Badge, PageHeader } from '../components/Parts';
import { dateTime, inr, todayIst } from '../format';

const errorText = (e: unknown) => (e instanceof ApiError ? e.message : String(e));

function Details({ r }: { r: CertRequestRow }) {
  const { t } = useTranslation();
  if (r.type === 'MEMBERSHIP') return <>{t('cert.purpose')}: {(r.details as MembershipDetails).purpose}</>;
  if (r.type === 'NIKAH_NOC') {
    const d = r.details as NikahNocDetails;
    return <>{t('cert.spouse')}: {d.spouseName} · {d.spouseMahallu}{d.nikahDate ? ` · ${d.nikahDate}` : ''}</>;
  }
  const d = r.details as DeathBurialDetails;
  return <>{t('cert.died')}: {d.dateOfDeath} · {t('cert.buried')}: {d.burialPlace}, {d.burialDate}</>;
}

/** Record a request for one member: find the house, choose the person, fill what the type needs. */
function RequestForm({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [type, setType] = useState<CertType | ''>('');
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  const [houseId, setHouseId] = useState('');
  const [memberId, setMemberId] = useState('');
  const [f, setF] = useState<Record<string, string>>({});
  const [note, setNote] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setTerm(q.trim()), 300);
    return () => clearTimeout(id);
  }, [q]);
  const houses = useHouses({ q: term || undefined, page: 1, size: 10 });
  const house = useHouse(houseId || null);
  const set = (k: string) => (v: string) => setF((p) => ({ ...p, [k]: v }));
  const details = (): CertDetails =>
    type === 'MEMBERSHIP'
      ? { purpose: f.purpose ?? '' }
      : type === 'NIKAH_NOC'
        ? { spouseName: f.spouseName ?? '', spouseMahallu: f.spouseMahallu ?? '', nikahDate: f.nikahDate || null }
        : { dateOfDeath: f.dateOfDeath ?? '', placeOfDeath: f.placeOfDeath || null, burialDate: f.burialDate ?? '', burialPlace: f.burialPlace ?? '' };
  const save = useMutation({
    mutationFn: () => certs.request({ type: type as CertType, memberId, details: details(), note: note.trim() || null }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['certs'] });
      onClose();
    },
  });
  const members = (house.data?.members ?? []).filter((m) => (type === 'DEATH_BURIAL' ? m.status === 'DECEASED' : m.status === 'ALIVE'));
  return (
    <Modal title={t('cert.new')} onClose={onClose} wide footer={<FormFooter onCancel={onClose} saving={save.isPending} saveLabel={t('cert.record')} error={save.isError ? errorText(save.error) : undefined} />}>
      <form
        id="active-form"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <FormSection title={t('cert.section')}>
          <SelectField<CertType> label={t('cert.type')} value={type} onChange={(v) => { setType(v); setMemberId(''); }} required empty={t('form.choose')} options={CERT_TYPES.map((x) => ({ value: x, label: t(`ctype.${x}`) }))} />
          <TextField label={t('collect.find')} type="search" value={q} onChange={setQ} maxLength={100} />
          <SelectField
            label={t('col.house')}
            value={houseId}
            onChange={(v) => { setHouseId(v); setMemberId(''); }}
            required
            empty={t('form.choose')}
            options={(houses.data?.rows ?? []).map((h) => ({ value: h.id, label: `${h.houseNo} · ${h.nameEn} · ${h.headName ?? ''}` }))}
          />
          <SelectField
            label={t('requests.person')}
            value={memberId}
            onChange={setMemberId}
            required
            empty={t('form.choose')}
            hint={type === 'DEATH_BURIAL' ? t('cert.deceasedHint') : undefined}
            options={members.map((m) => ({ value: m.id, label: `${m.nameEn} · ${t(`rel.${m.relation}`)}${m.birthYear ? ` · ${m.birthYear}` : ''}` }))}
          />
        </FormSection>
        {type ? (
          <FormSection title={t(`ctype.${type}`)}>
            {type === 'MEMBERSHIP' ? <TextField label={t('cert.purpose')} value={f.purpose ?? ''} onChange={set('purpose')} maxLength={CERT_LIMITS.text} required full hint={t('cert.purposeHint')} /> : null}
            {type === 'NIKAH_NOC' ? (
              <>
                <TextField label={t('cert.spouseName')} value={f.spouseName ?? ''} onChange={set('spouseName')} maxLength={CERT_LIMITS.text} required />
                <TextField label={t('cert.spouseMahallu')} value={f.spouseMahallu ?? ''} onChange={set('spouseMahallu')} maxLength={CERT_LIMITS.text} required />
                <TextField label={t('cert.nikahDate')} type="date" value={f.nikahDate ?? ''} onChange={set('nikahDate')} />
                <p className="note full">{t('cert.nikahNote')}</p>
              </>
            ) : null}
            {type === 'DEATH_BURIAL' ? (
              <>
                <TextField label={t('cert.dateOfDeath')} type="date" max={todayIst()} value={f.dateOfDeath ?? ''} onChange={set('dateOfDeath')} required />
                <TextField label={t('cert.placeOfDeath')} value={f.placeOfDeath ?? ''} onChange={set('placeOfDeath')} maxLength={CERT_LIMITS.text} />
                <TextField label={t('cert.burialDate')} type="date" value={f.burialDate ?? ''} onChange={set('burialDate')} required />
                <TextField label={t('cert.burialPlace')} value={f.burialPlace ?? ''} onChange={set('burialPlace')} maxLength={CERT_LIMITS.text} required />
              </>
            ) : null}
            <TextAreaField label={t('cert.note')} value={note} onChange={setNote} maxLength={CERT_LIMITS.note} rows={2} full />
          </FormSection>
        ) : null}
      </form>
    </Modal>
  );
}

function ReasonForm({ title, hint, onSave, onClose }: { title: string; hint: string; onSave: (reason: string) => Promise<unknown>; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: () => onSave(reason.trim()),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['certs'] });
      onClose();
    },
  });
  return (
    <Modal title={title} onClose={onClose} footer={<FormFooter onCancel={onClose} saving={save.isPending} saveLabel={title} error={save.isError ? errorText(save.error) : undefined} />}>
      <form
        id="active-form"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <p className="callout small">{hint}</p>
        <FormSection title={title}>
          <TextAreaField label={t('rcpt.reason')} value={reason} onChange={setReason} maxLength={300} rows={3} required full />
        </FormSection>
      </form>
    </Modal>
  );
}

/** Certificates › Requests (pilot slice 9): record, sign twice, issue; reject with a reason. */
export function CertRequests() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const me = useMe();
  const canRequest = Boolean(me.data?.role && CERT_ROLES.includes(me.data.role));
  const [state, setState] = useState('REQUESTED');
  const list = useCertRequests(state);
  const [adding, setAdding] = useState(false);
  const [rejecting, setRejecting] = useState<CertRequestRow | null>(null);
  const sign = useMutation({ mutationFn: (r: CertRequestRow) => certs.sign(r.id, r.version), onSuccess: () => qc.invalidateQueries({ queryKey: ['certs'] }) });
  return (
    <>
      <PageHeader icon="file" title={t('cert.requestsTitle')} desc={t('cert.requestsDesc')} actions={canRequest ? [{ label: t('cert.new'), icon: 'plus', primary: true, onClick: () => setAdding(true) }] : []} />
      <div className="filters" role="group">
        {(['REQUESTED', 'ISSUED', 'REJECTED', ''] as const).map((s) => (
          <button key={s} type="button" className={`btn sm${state === s ? ' primary' : ''}`} aria-pressed={state === s} onClick={() => setState(s)}>
            {s ? t(`creq.${s}`) : t('delivery.all')}
          </button>
        ))}
      </div>
      {sign.isError ? <p className="form-error" role="alert">{errorText(sign.error)}</p> : null}
      {list.isError ? <p className="form-error">{t(loadErrorKey(list.error))}</p> : null}
      <div className="privacy-grid" style={{ marginTop: 12 }}>
        {(list.data ?? []).map((r) => (
          <section key={r.id} className="card">
            <div className="section-h">
              <h2>{t(`ctype.${r.type}`)}</h2>
              <Badge tone={r.state === 'ISSUED' ? 'ok' : r.state === 'REJECTED' ? 'neutral' : 'warn'}>{t(`creq.${r.state}`)}</Badge>
              <span className="note">{t('cert.signatures', { n: r.signatures.length })}</span>
              <span className="spacer" />
              {r.canSign ? (
                <button type="button" className="btn sm primary" disabled={sign.isPending} onClick={() => sign.mutate(r)}>
                  <Icon name="check" />
                  {t('cert.sign')}
                </button>
              ) : null}
              {canRequest && r.state === 'REQUESTED' ? (
                <button type="button" className="btn sm" onClick={() => setRejecting(r)}>
                  {t('requests.reject')}
                </button>
              ) : null}
            </div>
            <dl className="kv">
              <div>
                <dt>{t('requests.person')}</dt>
                <dd>
                  {r.subject.nameEn} {r.subject.nameMl ? <span className="ml note">{r.subject.nameMl}</span> : null}
                  <div className="note num">{r.subject.house.houseNo} · {r.subject.house.nameEn}</div>
                </dd>
              </div>
              <div>
                <dt>{t('cert.details')}</dt>
                <dd>
                  <Details r={r} />
                </dd>
              </div>
              <div>
                <dt>{t('cert.requestedBy')}</dt>
                <dd>
                  {r.requestedBy} · {dateTime(r.requestedAt, i18n.language)}
                </dd>
              </div>
              <div>
                <dt>{t('account.arrears')}</dt>
                <dd className="num">
                  {inr(r.houseArrearsInr)} <span className="note">{t('cert.arrearsInfo')}</span>
                </dd>
              </div>
            </dl>
            {r.signatures.length ? (
              <ul className="history" style={{ marginTop: 10 }}>
                {r.signatures.map((s, i) => (
                  <li key={i}>
                    <b>{s.signerName}</b> · {t(`role.${s.signerRole}`)} · {dateTime(s.signedAt, i18n.language)} · <span className="num note">{s.contentHash}</span>
                  </li>
                ))}
              </ul>
            ) : null}
            {r.rejectReason ? <p className="note">{r.rejectReason}</p> : null}
          </section>
        ))}
        {list.data && !list.data.length ? <p className="note">{t('cert.none')}</p> : null}
      </div>
      {adding ? <RequestForm onClose={() => setAdding(false)} /> : null}
      {rejecting ? <ReasonForm title={t('requests.reject')} hint={t('cert.rejectHint')} onSave={(reason) => certs.reject(rejecting.id, reason, rejecting.version)} onClose={() => setRejecting(null)} /> : null}
    </>
  );
}

/** Certificates › Issued (pilot slice 9): the issued register, PDFs and withdrawals. */
export function CertIssued() {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const canWithdraw = Boolean(me.data?.role && CERT_ROLES.includes(me.data.role));
  const list = useIssuedCertificates();
  const [withdrawing, setWithdrawing] = useState<CertificateRow | null>(null);
  const [error, setError] = useState('');
  return (
    <>
      <PageHeader icon="file" title={t('cert.issuedTitle')} desc={t('cert.issuedDesc')} />
      {error ? <p className="form-error" role="alert">{error}</p> : null}
      {list.isError ? <p className="form-error">{t(loadErrorKey(list.error))}</p> : null}
      <div className="tbl-wrap">
        <div className="tbl-scroll">
          <table>
            <thead>
              <tr>
                <th>{t('cert.serial')}</th>
                <th>{t('cert.type')}</th>
                <th>{t('requests.person')}</th>
                <th>{t('cert.issued')}</th>
                <th>PDF</th>
                <th>{t('rcpt.status')}</th>
                <th>{t('col.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {(list.data ?? []).map((c) => (
                <tr key={c.id} className={c.state === 'WITHDRAWN' ? 'muted-row' : undefined}>
                  <td className="num">{c.serial}</td>
                  <td>{t(`ctype.${c.type}`)}</td>
                  <td>
                    {c.subject.nameEn} <span className="note num">{c.subject.houseNo}</span>
                  </td>
                  <td>{dateTime(c.issuedAt, i18n.language)}</td>
                  <td>
                    <Badge tone={c.pdf.state === 'READY' ? 'ok' : c.pdf.state === 'FAILED' ? 'bad' : 'neutral'}>{t(`pdfstate.${c.pdf.state}`)}</Badge>
                    {c.pdf.error ? <div className="note">{c.pdf.error}</div> : null}
                    {c.pdf.sha256 ? <div className="note num" title={c.pdf.sha256}>SHA-256 {c.pdf.sha256.slice(0, 12)}…</div> : null}
                  </td>
                  <td>
                    <Badge tone={c.state === 'ISSUED' ? 'ok' : 'neutral'}>{t(`cstate.${c.state}`)}</Badge>
                    {c.withdrawReason ? <div className="note">{c.withdrawReason}</div> : null}
                  </td>
                  <td className="row-actions">
                    {c.pdf.state === 'READY' ? (
                      <button type="button" className="btn sm" onClick={() => certs.pdf(c.id, c.serial).catch((e: unknown) => setError(errorText(e)))}>
                        <Icon name="down" />
                        PDF
                      </button>
                    ) : null}
                    {canWithdraw && c.state === 'ISSUED' ? (
                      <button type="button" className="btn sm" onClick={() => setWithdrawing(c)}>
                        {t('cert.withdraw')}
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
              {list.data && !list.data.length ? (
                <tr>
                  <td colSpan={7} className="note">
                    {t('cert.none')}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
      {withdrawing ? <ReasonForm title={t('cert.withdraw')} hint={t('cert.withdrawHint')} onSave={(reason) => certs.withdraw(withdrawing.id, reason, withdrawing.version)} onClose={() => setWithdrawing(null)} /> : null}
    </>
  );
}

/** The public page a certificate's QR code opens: minimal data, no sign-in. */
export function VerifyCertificate() {
  const { t } = useTranslation();
  const { token = '' } = useParams();
  const q = usePublicCertificate(token);
  const c = q.data;
  return (
    <main className="public-page">
      <header>
        <h1>{t('verify.title')}</h1>
      </header>
      {q.isPending ? <p className="note">{t('drawer.loading')}</p> : null}
      {q.isError ? <p className="callout">{t('verify.notFound')}</p> : null}
      {c ? (
        <article className="card">
          <p className="callout" role="status">
            {c.state === 'ISSUED' ? t('verify.genuine') : t('verify.withdrawn', { date: c.withdrawnOn })}
          </p>
          <dl className="kv" style={{ marginTop: 12 }}>
            <div>
              <dt>{t('verify.mahallu')}</dt>
              <dd>
                {c.mahallu.nameEn} {c.mahallu.nameMl ? <span className="ml">· {c.mahallu.nameMl}</span> : null}
              </dd>
            </div>
            <div>
              <dt>{t('cert.type')}</dt>
              <dd>{t(`ctype.${c.type}`)}</dd>
            </div>
            <div>
              <dt>{t('cert.serial')}</dt>
              <dd className="num">{c.serial}</dd>
            </div>
            <div>
              <dt>{t('cert.issued')}</dt>
              <dd className="num">{c.issuedOn}</dd>
            </div>
            <div>
              <dt>{t('requests.person')}</dt>
              <dd>
                {c.nameEn} {c.nameMl ? <span className="ml">· {c.nameMl}</span> : null}
              </dd>
            </div>
            {c.pdfSha256 ? (
              <div>
                <dt>SHA-256</dt>
                <dd className="num" style={{ wordBreak: 'break-all' }}>
                  {c.pdfSha256}
                </dd>
              </div>
            ) : null}
          </dl>
          <p className="note" style={{ marginTop: 12 }}>
            {t('verify.hint')}
          </p>
        </article>
      ) : null}
      <PoweredBy />
    </main>
  );
}
